/**
 * One group: what it is, who is in it, and how many of them a campaign reaches.
 *
 * The member list is the contacts endpoint filtered to this group, so consent,
 * addresses and paging behave exactly as they do on the Contacts screen —
 * rendered here rather than by sending someone to a filtered Contacts view,
 * because a group is a thing you inspect, not a saved search.
 *
 * The two counts at the top are the point of the screen. `member_count` is how
 * many people are in the group; `sendable_count` is how many have an email
 * address that is explicitly subscribed, which is what a campaign will actually
 * reach. Planning a send against the first number is how a list of five hundred
 * turns out to be three hundred on the day.
 */

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { Consent, isSendable } from "../components/Consent";
import { Metric } from "../components/Metric";
import { Icon } from "../components/Sprites";
import { useSession } from "../session";
import type { Contact, CursorPage, Group } from "../api/types";

const PAGE_SIZE = 50;

export function GroupDetail() {
  const { groupId = "" } = useParams();
  const { orgId, can } = useSession();
  const navigate = useNavigate();
  const writable = can("contacts:write");

  const [history, setHistory] = useState<string[]>([]);
  const cursor = history.at(-1) ?? "";

  const group = useAsync<Group>(
    (signal) => api.get<Group>(`/v1/groups/${encodeURIComponent(groupId)}`, { signal }),
    [orgId, groupId],
  );

  const members = useAsync<CursorPage<Contact>>(
    (signal) =>
      api.get<CursorPage<Contact>>("/v1/contacts", {
        signal,
        query: { limit: PAGE_SIZE, cursor, group_id: groupId },
      }),
    [orgId, groupId, cursor],
  );

  if (group.error) {
    return (
      <>
        <div className="topbar">
          <h1 className="oc-headline-lg">Group</h1>
        </div>
        <div className="content">
          <div className="oc-panel oc-starfield" style={{ flex: 1 }}>
            <div className="oc-empty">
              <div className="oc-empty__title">Could not load this group</div>
              <div className="oc-empty__hint">{group.error.message}</div>
              <button type="button" className="oc-btn oc-btn--sm" onClick={() => navigate("/groups")}>
                Back to groups
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const data = group.data;
  const rows = members.data?.data ?? [];

  return (
    <>
      <div className="topbar">
        <div className="row" style={{ gap: "var(--oc-space-3)", minWidth: 0 }}>
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--icon oc-btn--sm"
            aria-label="Back to groups"
            onClick={() => navigate("/groups")}
          >
            <Icon name="chevron" size={14} className="icon--back" />
          </button>
          <h1 className="oc-headline-lg oc-truncate">{data?.name ?? "Group"}</h1>
        </div>
        <button
          type="button"
          className="oc-btn oc-btn--ghost oc-btn--sm"
          onClick={() => {
            group.reload();
            members.reload();
          }}
        >
          <Icon name="refresh" size={12} />
          Refresh
        </button>
      </div>

      <div className="content">
        <div className="stack" style={{ flex: 1, minWidth: 0, gap: "var(--oc-gutter)" }}>
          {data && (
            <div className="oc-panel">
              <div className="oc-panel__body">
                {data.description && (
                  <p className="oc-body-sm oc-text-secondary" style={{ marginTop: 0 }}>
                    {data.description}
                  </p>
                )}

                <div className="metrics">
                  <Metric label="Members" value={data.member_count} />
                  <Metric
                    label="Sendable"
                    value={data.sendable_count ?? 0}
                    note={
                      data.sendable_count != null && data.sendable_count < data.member_count
                        ? `${(data.member_count - data.sendable_count).toLocaleString()} not subscribed`
                        : undefined
                    }
                  />
                </div>

                <p className="oc-body-sm oc-text-secondary" style={{ marginBottom: 0 }}>
                  Sendable counts members with an email address that is explicitly
                  subscribed — what a campaign to this group would reach today. Unknown
                  consent is not permission, so it is not counted.
                </p>
              </div>
            </div>
          )}

          <div className="oc-panel oc-starfield" style={{ flex: 1, minWidth: 0 }}>
            <div className="oc-table group-members-table" style={{ flex: 1, minHeight: 0 }}>
              <div className="oc-table__head" role="row">
                <span>Name</span>
                <span>Addresses</span>
                <span>Consent</span>
                <span className="oc-table__cell--right">Action</span>
              </div>

              <div className="oc-table__body">
                {members.error && (
                  <p className="field-error" style={{ padding: "var(--oc-gutter)" }}>
                    {members.error.message}
                  </p>
                )}

                {members.loading && rows.length === 0 && <SkeletonRows columns={4} />}

                {!members.error && rows.length === 0 && !members.loading && (
                  <div className="oc-empty">
                    <div className="oc-empty__title">Nobody in this group yet</div>
                    <div className="oc-empty__hint">
                      Add people from the Contacts screen — open a contact and use its
                      Groups section, or select several and add them at once.
                    </div>
                    <button
                      type="button"
                      className="oc-btn oc-btn--sm"
                      onClick={() => navigate("/contacts")}
                    >
                      Go to contacts
                    </button>
                  </div>
                )}

                {rows.map((contact) => (
                  <MemberRow
                    key={contact.id}
                    contact={contact}
                    groupId={groupId}
                    removable={writable}
                    onRemoved={() => {
                      group.reload();
                      members.reload();
                    }}
                    onOpen={() => navigate(`/contacts/${contact.id}`)}
                  />
                ))}
              </div>

              <div className="oc-table__footer">
                <span className="oc-mono-sm">
                  {rows.length === 0 ? "No members" : `${rows.length} shown`}
                </span>
                <span style={{ display: "flex", gap: "var(--oc-space-2)" }}>
                  <button
                    type="button"
                    className="oc-btn oc-btn--ghost oc-btn--sm"
                    disabled={history.length === 0}
                    onClick={() => setHistory((h) => h.slice(0, -1))}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="oc-btn oc-btn--ghost oc-btn--sm"
                    disabled={!members.data?.has_more || !members.data.next_cursor}
                    onClick={() => {
                      const next = members.data?.next_cursor;
                      if (next) setHistory((h) => [...h, next]);
                    }}
                  >
                    Next
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function MemberRow({
  contact,
  groupId,
  removable,
  onRemoved,
  onOpen,
}: {
  contact: Contact;
  groupId: string;
  removable: boolean;
  onRemoved: () => void;
  onOpen: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.delete(
        `/v1/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(contact.id)}`,
      );
      onRemoved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not remove from the group.");
      setBusy(false);
    }
  };

  return (
    <div
      className="oc-table__row"
      role="row"
      tabIndex={0}
      style={{ cursor: "pointer" }}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <span className="oc-body-sm oc-truncate">
        {contact.name || <span className="oc-text-muted">No name</span>}
        {error && (
          <span className="field-error" role="alert">
            {error}
          </span>
        )}
      </span>

      <span style={{ minWidth: 0 }}>
        {contact.identities.map((identity) => (
          <span key={identity.id} className="oc-mono-sm oc-truncate" style={{ display: "block" }}>
            {/* Muted when a campaign would not reach it — the address is still
                shown, because it is how you find the person, but it should not
                read as reachable when it is not. Same rule as Contacts. */}
            <span className={isSendable(identity.consent_status) ? undefined : "oc-text-muted"}>
              {identity.address}
            </span>
          </span>
        ))}
      </span>

      <span>
        {contact.identities.map((identity) => (
          <Consent key={identity.id} status={identity.consent_status} />
        ))}
      </span>

      <span className="oc-table__cell--right">
        {removable && (
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--sm"
            disabled={busy}
            // Stopped, or removing someone also opens their contact drawer.
            onClick={(event) => {
              event.stopPropagation();
              void remove();
            }}
          >
            {busy ? "Removing…" : "Remove"}
          </button>
        )}
      </span>
    </div>
  );
}
